/**
 * The community sidebar: the Attractr team as a flat list the Avatar
 * component can render, plus the marketplace's total member count.
 *
 * Both CommunityPage and PostPage ducks dispatch this from their
 * loadData. The data is identical between the two pages, but we
 * keep the slice on each page so the redux shape stays consistent
 * with how the rest of the codebase handles page-local fetches.
 *
 * Resolved on the server by /api/community/team, which keeps the team's
 * email addresses out of the client bundle and caches the result for an
 * hour. It used to resolve each email from here through the open users
 * endpoint: 7 requests per uncached render.
 */

import { usersAPI } from '../api/clients/usersAPI';

// Module-level cache, still useful on the SERVER: Community and Post pages
// dispatch this from loadData, and parallel renders should await one
// in-flight request instead of each firing their own.
const SIDEBAR_CACHE_TTL_MS = 5 * 60 * 1000;
let sidebarCache = { promise: null, expiresAt: 0 };

const fetchCommunitySidebarDataUncached = async () => {
  const res = await usersAPI.communityTeam();
  const { team = [], memberCount = 0 } = res?.data?.data || {};
  return { team, memberCount };
};

/**
 * Returns { team, memberCount }, cached for 5 minutes per process.
 */
export const fetchCommunitySidebarData = () => {
  const now = Date.now();
  if (sidebarCache.promise && now < sidebarCache.expiresAt) {
    return sidebarCache.promise;
  }
  const promise = fetchCommunitySidebarDataUncached().catch(e => {
    // Don't cache failures — clear so the next caller retries.
    if (sidebarCache.promise === promise) {
      sidebarCache = { promise: null, expiresAt: 0 };
    }
    throw e;
  });
  sidebarCache = { promise, expiresAt: now + SIDEBAR_CACHE_TTL_MS };
  return promise;
};
